import { AnswerStatuses, IMeetingConfirmation } from './../../utils/types/meetingConfirmation';
import {
  AuthService, CalendarRequest, DATE_FORMAT, getEndInterval,
  getMeetingFromDto, getMeetingWithLayer, getStartInterval,
  IMeetingsForUsersResponse, IOccupiedTime, IUser, IUserForMeetingPlaner,
  monthsInGenitiveCase, TSplitedMeetings, TUserForMeetingPlaner
} from '@utils';
import { addHours, format, subHours } from 'date-fns';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { Inject, Service } from 'typedi';
import { MembersStore } from '../../store';
import { CalendarViewModel } from '../calendarPage';
import { ProfileViewModel } from '../profilePage';
import { MeetingViewModel } from '@components';

@Service()
export class MeetingPlanerViewModel {

  @Inject(() => CalendarRequest)
  private calendarRequest: CalendarRequest;

  @Inject(() => CalendarViewModel)
  private calendarViewModel: CalendarViewModel;

  @Inject(() => ProfileViewModel)
  private profileViewModel: ProfileViewModel;

  @Inject(() => MeetingViewModel)
  private meetingViewModel: MeetingViewModel;

  @Inject(() => MembersStore)
  private membersStore: MembersStore;

  @observable date = new Date(localStorage.getItem('date')) ?? new Date();

  @observable.shallow selectedMembers: TUserForMeetingPlaner =
    new Map((JSON.parse(sessionStorage.getItem('members')) as TUserForMeetingPlaner) ?? []);

  @observable.shallow memberMeetings: TSplitedMeetings = new Map();

  @observable isLoading = false;

  @observable draggingTimeStart: Date = new Date(sessionStorage.getItem('startDragTime'));

  @observable draggingTimeEnd: Date = new Date(sessionStorage.getItem('endDragTime'));

  @computed get membersValues() {
    const members: IUserForMeetingPlaner[] = [];
    for (const member of this.selectedMembers.values())
      member.id !== AuthService.getUserId() && members.push(member);
    return members;
  }

  @computed get title() {
    return `${this.date.getDate()} 
  ${monthsInGenitiveCase[this.date.getMonth()]}, 
  ${this.date.getFullYear()} 
  ${this.isDayActive(this.date) ? 'Сегодня' : ''}`;
  }

  @computed get occupiedTime() {
    const data: IOccupiedTime[] = [];
    for (let meetings of this.memberMeetings.entries()) {
      const currentUserId = AuthService.getUserId();

      meetings[1].forEach(meeting =>
        (this.selectedMembers.has(meetings[0] as number) || meetings[0] === currentUserId)
        && data.push({ start: meeting.startDate, end: meeting.endDate })
      );
    }
    return data;
  }

  isDayActive = (day: Date) => format(new Date(), DATE_FORMAT) === format(day, DATE_FORMAT);

  saveMembers = () => {
    localStorage.setItem('savedMembers', JSON.stringify(this.selectedMembers));
  };

  saveTime = () => this.meetingViewModel.saveTime(this.draggingTimeStart, this.draggingTimeEnd);

  constructor() {
    makeObservable(this);

    reaction(
      () => this.selectedMembers.size,
      () => {
        sessionStorage.setItem('members', JSON.stringify(this.selectedMembers));
      }
    );
  }

  @action setDraggingTime = (startTime: Date, endTime: Date) => {
    this.draggingTimeStart = new Date(startTime);
    this.draggingTimeEnd = new Date(endTime);

    sessionStorage.setItem('startDragTime', `${this.draggingTimeStart}`);
    sessionStorage.setItem('endDragTime', `${this.draggingTimeEnd}`);
  }

  @action getAllMeetingsByUsers = () => {
    this.isLoading = true;
    const currentUserId = AuthService.getUserId();
    const userIds = [currentUserId, ... this.membersValues.map(({ id }) => id)];

    this.calendarRequest.getAllMeetings({
      startIntervalDate: getStartInterval(this.date, 'day').toISOString(),
      endIntervalDate: getEndInterval(this.date, 'day').toISOString(),
      userIds,
      requestMeeting: 'ALL'
    }).then((res) => {
      const meetingStartHour = this.meetingViewModel.meetingData.startDate.getHours();
      let startHour = meetingStartHour < 8 ? meetingStartHour : 8;
      userIds.forEach((id) => {
        const meetings = res.data as IMeetingsForUsersResponse;
        meetings.meetingsForUsers[id].forEach(
          (item) => {
            const meetingAfterDto = getMeetingFromDto(item, this.profileViewModel.minutesOffset);
            const meeting = getMeetingWithLayer(0, meetingAfterDto, this.memberMeetings.get(id));
            runInAction(() => (
              !this.memberMeetings.get(id)?.find(item => item.id === meeting.id) && this.memberMeetings.set(id,
                [...this.memberMeetings.get(id) ?? [], meeting]
              )
            ));
            meeting.startDate.getHours() < startHour && (startHour = meeting.startDate.getHours());
            return meeting;
          },
        );
      });
      this.calendarViewModel.setStartHour(startHour);
      this.isLoading = false;
    });
  };

  @action setMembers = (members: IUser[], confirmations: IMeetingConfirmation[] | undefined) => {
    members.map((member) => (
      this.selectedMembers.set(member.id, {
        isSelected: true,
        confirmation: confirmations?.find((elem) => elem.userId === member.id) ?? null,
        ...member,
      })
    ));
  };

  @action updateSelectedMembers = (memberId: number) =>
    this.selectedMembers.has(memberId)
      ? this.selectedMembers.delete(memberId)
      : this.selectedMembers.set(memberId, {
        isSelected: true,
        ...this.membersStore.getMemberById(memberId)
      });

  @action clearMembers = () => {
    this.selectedMembers.clear();
    sessionStorage.removeItem('members');
  };

  clearDate = () => {
    localStorage.removeItem('date');
    sessionStorage.removeItem('startDragTime');
    sessionStorage.removeItem('endDragTime');
  }

  @action setDate = (date: Date) => {
    this.date = date;
    localStorage.setItem('date', date.toString());
  };

  @action moveDate = (direction: 'next' | 'prev') => {
    const today = this.calendarViewModel.today;
    today.setHours(0, 0, 0, 0);
    if (direction === 'next' || +today < +this.date - 86_400_000) { // вычитаем день
      this.date = (direction === 'prev' ? subHours : addHours)(this.date, 24);
      this.clearMeetings();
      this.getAllMeetingsByUsers();
    }
  };

  saveDate = () => localStorage.setItem('savedDate', this.date.toString());

  /**
   * Обновление ответов у пользователя.
   * @param memberId id участника встречи, чье предложение о времени мы отврегли.
   */
  @action cancelOffer = (memberId: number) => {
    const newMember = this.selectedMembers.get(memberId);
    newMember.confirmation.answerStatus = AnswerStatuses.COUNTER_OFFER_REJECTED;
    this.selectedMembers.set(memberId, newMember);
    sessionStorage.setItem('members', JSON.stringify(this.selectedMembers));
  };

  @action clearMeetings = () => this.memberMeetings.clear();

  isUserInMembers = (user: IUser) => this.selectedMembers.has(user.id);
}
